import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

interface Stage {
  id: string;
  name: string;
  icon: string;
  description: string;
}

const stages: Stage[] = [
  { id: 'UPLOAD', name: '上传照片', icon: '📸', description: '验证照片格式和清晰度' },
  { id: 'CHARACTER', name: '生成角色', icon: '👤', description: 'AI分析照片，创建专属角色' },
  { id: 'STORY', name: '创作故事', icon: '✍️', description: '根据主题编写精彩故事' },
  { id: 'IMAGE', name: '绘制插图', icon: '🎨', description: '为每页生成精美插图' },
  { id: 'AUDIO', name: '生成语音', icon: '🔊', description: '合成语音朗读故事' },
  { id: 'PDF', name: '生成PDF', icon: '📄', description: '整合所有内容生成PDF' },
  { id: 'COMPLETED', name: '完成', icon: '✅', description: '绘本生成完成！' },
];

function StoryProgress() {
  const { storyId } = useParams<{ storyId: string }>();
  const navigate = useNavigate();
  const [currentStage, setCurrentStage] = useState('UPLOAD');
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    // 模拟进度更新
    const interval = setInterval(() => {
      setCurrentStage(prev => {
        const currentIndex = stages.findIndex(s => s.id === prev);
        if (currentIndex < stages.length - 1) {
          const nextStage = stages[currentIndex + 1].id;
          setProgress(Math.round(((currentIndex + 1) / (stages.length - 1)) * 100));
          
          if (nextStage === 'COMPLETED') {
            setIsComplete(true);
            clearInterval(interval);
          }
          
          return nextStage;
        }
        return prev;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // 实际应该调用后端API获取真实进度
  useEffect(() => {
    if (storyId) {
      // 这里应该调用后端API获取进度
      // fetch(`http://localhost:3001/api/stories/${storyId}/progress`)
      console.log('获取故事进度:', storyId);
    }
  }, [storyId]);

  const currentStageIndex = stages.findIndex(s => s.id === currentStage);

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      <h1 className="text-4xl font-bold text-center mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        ✨ 绘本生成中
      </h1>
      <p className="text-center text-gray-600 mb-8">
        AI正在为您创作独一无二的绘本，请稍候...
      </p>

      {/* 进度条 */}
      <div className="glass rounded-2xl p-8 mb-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-lg font-bold text-gray-700">总进度</span>
          <span className="text-2xl font-bold text-purple-600">{progress}%</span>
        </div>
        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          >
            <div className="h-full w-full bg-white/30 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* 阶段列表 */}
      <div className="space-y-4">
        {stages.map((stage, index) => {
          const isActive = index === currentStageIndex;
          const isCompleted = index < currentStageIndex;
          const isPending = index > currentStageIndex;

          return (
            <div
              key={stage.id}
              className={`
                glass rounded-xl p-6 transition-all duration-500
                ${isActive ? 'border-2 border-purple-500 shadow-lg scale-105' : ''}
                ${isCompleted ? 'opacity-70' : ''}
                ${isPending ? 'opacity-50' : ''}
              `}
            >
              <div className="flex items-center gap-4">
                {/* 状态图标 */}
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center text-2xl
                  ${isCompleted ? 'bg-green-500 text-white' : ''}
                  ${isActive ? 'bg-purple-500 text-white animate-pulse' : ''}
                  ${isPending ? 'bg-gray-200 text-gray-400' : ''}
                `}>
                  {isCompleted ? '✓' : stage.icon}
                </div>

                {/* 阶段信息 */}
                <div className="flex-1">
                  <h3 className={`
                    text-lg font-bold
                    ${isActive ? 'text-purple-700' : 'text-gray-800'}
                    ${isCompleted ? 'text-green-700' : ''}
                  `}>
                    {stage.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{stage.description}</p>
                </div>

                {/* 状态标签 */}
                <div className={`
                  px-3 py-1 rounded-full text-sm font-medium
                  ${isCompleted ? 'bg-green-100 text-green-700' : ''}
                  ${isActive ? 'bg-purple-100 text-purple-700' : ''}
                  ${isPending ? 'bg-gray-100 text-gray-500' : ''}
                `}>
                  {isCompleted && '已完成'}
                  {isActive && '进行中...'}
                  {isPending && '等待中'}
                </div>
              </div>

              {/* 当前阶段的额外信息 */}
              {isActive && (
                <div className="mt-4 pl-16">
                  <div className="flex items-center gap-2 text-purple-600">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    <span className="text-sm">AI正在处理中，请稍候...</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 完成后的操作 */}
      {isComplete && (
        <div className="mt-8 text-center animate-fade-in">
          <div className="glass rounded-2xl p-8 mb-6">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              绘本生成完成！
            </h2>
            <p className="text-gray-600 mb-6">
              您的专属绘本已经准备好了，快来查看吧！
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate(`/story/${storyId}`)}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-bold hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-105 shadow-lg"
              >
                📖 阅读绘本
              </button>
              <button
                onClick={() => navigate('/library')}
                className="px-8 py-3 bg-white text-purple-600 border-2 border-purple-600 rounded-full font-bold hover:bg-purple-50 transition-all"
              >
                📚 查看全部
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 提示信息 */}
      {!isComplete && (
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>⏱️ 预计需要 2-3 分钟，请耐心等待...</p>
          <p className="mt-1">您可以关闭此页面，稍后在"我的绘本"中查看结果</p>
        </div>
      )}
    </div>
  );
}

export default StoryProgress;
